import { collection } from 'infra/collection'
import { BusinessProfileVersion } from './BusinessProfileVersion'
import { BusinessProfileVersionDto } from './dtos'
import { BusinessProfilesAPI } from './API'
import { Id } from 'types/definitions'
import {
  CreateBusinessProfileInput,
  UpdateBusinessProfileInput,
  mapCreateBusinessProfileRequestToProto,
  mapUpdateBusinessProfileRequestToProto,
} from './businessProfileGrpcDtoMapper'

export default class BusinessProfileStore {
  businessProfiles = collection<
    BusinessProfileVersionDto,
    BusinessProfileVersion
  >({
    create: () => new BusinessProfileVersion(),
    getDataId: (data) => `${data.id}:${data.version}`,
    getModelId: (model) => `${model.id}:${model.version}`,
  })

  constructor(private api: BusinessProfilesAPI) {}

  /**
   * Gets business profile.
   */
  getBusinessProfile(id: Id, version: number) {
    return this.businessProfiles.get(`${id}:${version}`)
  }

  /**
   * Fetches business profile for a given job ID.
   */
  async fetchBusinessProfileForJob(jobId: Id) {
    return this.api.getBusinessProfileForJob(jobId).then((profile) => {
      return this.businessProfiles.set(profile)
    })
  }

  /**
   * Fetches business profile.
   */
  async fetchBusinessProfile(id: Id) {
    return this.api.getBusinessProfile(id).then((profile) => {
      return this.businessProfiles.set(profile)
    })
  }

  /**
   * Creates a new business profile.
   */
  async createBusinessProfile(input: CreateBusinessProfileInput) {
    const saved = await this.api.createBusinessProfile(
      mapCreateBusinessProfileRequestToProto(input),
    )
    this.businessProfiles.set({
      id: saved.id,
      version: saved.version,
      workspaceId: Id.assert(input.workspaceId),
      ownerUserId: Id.assert(input.ownerUserId),
      businessInfo: input.businessInfo,
      contactInfo: input.contactInfo,
    })
    return saved
  }

  /**
   * Updates an existing business profile.
   */
  async updateBusinessProfile(
    input: UpdateBusinessProfileInput & {
      workspaceId: Id
      ownerUserId: Id
    },
  ) {
    const saved = await this.api.updateBusinessProfile(
      mapUpdateBusinessProfileRequestToProto(input),
    )
    this.businessProfiles.set({
      id: saved.id,
      version: saved.version,
      workspaceId: Id.assert(input.workspaceId),
      ownerUserId: Id.assert(input.ownerUserId),
      businessInfo: input.businessInfo,
      contactInfo: input.contactInfo,
    })
    return saved
  }
}
